import { preview } from './preview.js';
import { dehydrate, hydrate, type Query } from '@tanstack/react-query';
import type { MeDto } from '@werft/contracts';
import { keys, queryClient } from './queries.js';

/**
 * Zuletzt Gesehenes ohne Netz (Paket P10): der Query-Stand der angemeldeten Person liegt in IndexedDB und wird beim
 * Start eingelesen, bevor die Oberfläche zeichnet. Danach lädt TanStack Query wie gewohnt frisch nach; ohne Netz
 * bleibt der gespeicherte Stand sichtbar. Änderungen ohne Netz übernimmt der Ausgangskorb (ADR 0007).
 *
 * Schutz: gespeichert wird nur für EINE Person (Konto-ID im Datensatz). Abmelden, eine andere Anmeldung oder
 * „nicht angemeldet“ löschen den Stand. Entzogene Projekte verschwinden mit dem nächsten Speichern (removeQueries).
 */
const DB = 'werft';
const STORE = 'cache';
const KEY = 'queries';
const MAX_AGE = 7 * 24 * 3_600_000;
/** Neuer Wert, wenn sich das Format der Daten so ändert, dass alte Stände nicht mehr passen */
const BUSTER = 'boardposition-v1'; // 0.166: Aufgaben haben `boardPosition` (ADR 0172).

interface Saved {
  userId: string;
  at: number;
  buster: string;
  state: ReturnType<typeof dehydrate>;
}

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  const db = await open();
  if (!db) return undefined;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(undefined);
    tx.oncomplete = () => db.close();
  });
}

/** Welche Daten offline lesbar sein sollen: Konto, Workspaces, Projekte, Mein Tag – keine Suche, keine Einladungen */
function wanted(q: Query): boolean {
  if (q.state.status !== 'success' || q.state.data === undefined) return false;
  const [a, , c] = q.queryKey as unknown[];
  if (a === 'me' || a === 'config') return true;
  // Sehr große Projekte nicht vollständig: höchstens die ersten Seiten (je 200 Aufgaben) – Speichern bleibt billig
  // ADR 0080: Personendaten Dritter nie auf dem Gerät ablegen
  if (a === 'p' && c === 'people') return false;
  // ADR 0082: Kosten bleiben ebenfalls nicht auf dem Gerät
  if (a === 'p' && c === 'budget') return false;
  // ADR 0085: Zeiten sind persönlich – nicht auf dem Gerät
  if (a === 'p' && c === 'time') return false;
  // ADR 0091: Statusbericht enthält Budgetsummen – nicht auf dem Gerät
  if (a === 'p' && c === 'report') return false;
  // ADR 0116/0117: Verweise mit Kontakten, Budgetpositionen oder Einsätzen bleiben nicht auf dem Gerät
  if (a === 'p' && c === 'links' && ((q.state.data as { items?: { kind: string }[] }).items ?? []).some((x) => x.kind === 'contact' || x.kind === 'budget' || x.kind === 'shift')) return false;
  if (a === 'p') return !(c === 'tasks' && ((q.state.data as { pages?: unknown[] }).pages?.length ?? 0) > 2);
  if (a === 'ws') return c === undefined || c === 'projects' || c === 'members';
  if (a === 'inbox') return true;
  return false;
}

let saving: ReturnType<typeof setTimeout> | undefined;
let currentUser: string | null = null;

function scheduleSave() {
  if (saving) return;
  saving = setTimeout(() => {
    saving = undefined;
    // Im Leerlauf speichern, damit Tippen und Ziehen nicht ruckeln
    if ('requestIdleCallback' in window) requestIdleCallback(() => void save(), { timeout: 5000 });
    else void save();
  }, 2000);
}

async function save() {
  if (preview()) return; // P21: Gast-Sicht nie in den Speicher der Leitung
  const me = queryClient.getQueryData<MeDto | null>(keys.me);
  if (!me?.user) return;
  currentUser = me.user.id;
  const value: Saved = { userId: me.user.id, at: Date.now(), buster: BUSTER, state: dehydrate(queryClient, { shouldDehydrateQuery: wanted }) };
  await run('readwrite', (s) => s.put(value, KEY));
}

export async function clearPersisted() {
  currentUser = null;
  await run('readwrite', (s) => s.delete(KEY));
}

let pending: Saved | null = null;

function applySaved() {
  if (!pending) return;
  const saved = pending;
  pending = null;
  hydrate(queryClient, saved.state);
  // Gespeichertes ist nur ein Ersatzbild: alles gilt als veraltet und wird geladen, sobald das Netz zurück ist
  void queryClient.invalidateQueries({ refetchType: 'none' });
  currentUser = saved.userId;
}

/**
 * Beim Start: gespeicherten Stand bereithalten. Mit Netz wird wie gewohnt frisch geladen (kein veralteter Zwischenstand,
 * z. B. eine eben gezogene Aufgabe an alter Stelle). Erst wenn ein Laden am Netz scheitert – oder das Gerät schon beim
 * Start offline ist –, springt der gespeicherte Stand ein. Ab dann werden Änderungen gebündelt gespeichert.
 */
export async function restorePersisted(): Promise<void> {
  const saved = await run<Saved | undefined>('readonly', (s) => s.get(KEY) as IDBRequest<Saved | undefined>);
  if (saved && saved.buster === BUSTER && Date.now() - saved.at < MAX_AGE) pending = saved;
  else if (saved) await clearPersisted();
  if (pending && !navigator.onLine) applySaved();
  queryClient.getQueryCache().subscribe((e) => {
    // Netzfehler (Status 0) beim Laden: gespeicherten Stand einsetzen
    if (pending && e.type === 'updated' && e.action.type === 'failed' && (e.action.error as { status?: number } | null)?.status === 0) applySaved();
    if (e.type === 'removed' || e.type === 'updated' || e.type === 'added') {
      // Andere Person oder abgemeldet: nichts von der vorherigen behalten
      if (e.query.queryKey[0] === 'me' && e.type === 'updated') {
        const me = e.query.state.data as MeDto | null | undefined;
        if (e.query.state.status === 'success' && (!me || (currentUser && me.user.id !== currentUser))) {
          pending = null;
          void clearPersisted();
          if (me) scheduleSave();
          return;
        }
      }
      scheduleSave();
    }
  });
}
